import Router from 'next/router'
import React, { FormEvent, useEffect, useState } from 'react'

import { REGISTER_REF } from '@common/constants'
import { validateRegistrationFormData } from '@containers/auth/validators'
import {
  prepareRegistrationFormBody,
  registerUser,
} from '@containers/auth/utils'
import { showNoLaptopWarning } from '@lib/utils/auth'
import { FB_EVENTS } from '@lib/enums/tracking'

import RegistrationDetails from '@components/auth/registration-details'
import RatelimitErrorModal from '@components/auth/ratelimit-error-modal.dynamic'
import { showErrorAlert } from '@common/utils'
import { RecaptchaType } from '@lib/types/recaptcha'
import { UserProps } from '@contexts/session'
import useSegment, {
  getSegmentTrackingDict,
  SegmentEventEnum,
} from '@hooks/useSegment'
import { t } from '@lingui/macro'
declare global {
  interface Window {
    fbq: any
  }
}

type Props = {
  phone: string
  role: string
  next: string
}

type Query = {
  next?: string
  phone: string
  source: string
}

export const RegistrationDetailsContainer = (props: Props) => {
  const { role, next } = props
  const recaptchaRef = React.createRef<RecaptchaType>()
  const [registrationData, setRegistrationData] = useState({
    // User form inputs
    phone: props.phone,
    name: '',
    email: '',
    role: role,
    childName: '',
    childGrade: { value: 'select', label: 'Select grade/class' },
    childGender: { value: 'select', label: 'Select gender' },
    teacherEducationalQualification: {
      value: 'select',
      label: 'Select qualification',
    },
    teacherExperience: { value: 'select', label: 'Select experience' },
    teacherGender: { value: 'select', label: 'Select gender' },
    hasLaptop: null,
    childConsent: true,
    termsAndConditions: true,
    whatsappEnabled: true,

    // Server response
    response: {},
    errors: {},
    loading: false,
  })
  const [showRatelimitModal, setShowRatelimitModal] = useState(false)
  const track = useSegment()

  const changeRegistrationData = (data) => {
    setRegistrationData({ ...registrationData, ...data })
  }

  const handleInputChange = (elem: FormEvent<HTMLInputElement>): void => {
    let value: string | boolean = elem.currentTarget.value

    if (elem.currentTarget.name === 'hasLaptop') {
      value = elem.currentTarget.value === 'yes'

      if (!value) showNoLaptopWarning()
    }

    changeRegistrationData({
      [elem.currentTarget.name]: elem.currentTarget.value,
    })
  }

  const handleSelectChange = (
    id: string,
    value: {
      value: string
      label: string
    }
  ) => {
    changeRegistrationData({
      [id]: value,
    })
  }

  const handleCheckboxChange = (elem: FormEvent<HTMLInputElement>): void => {
    changeRegistrationData({
      [elem.currentTarget.name]: elem.currentTarget.checked,
    })
  }

  const handleCaptcha = async (key) => {
    // If the reCAPTCHA code is null or undefined indicating that
    // the reCAPTCHA was expired then return early
    if (!key) return

    // Else reCAPTCHA was executed successfully so proceed with the signup
    const formBody = prepareRegistrationFormBody(registrationData)
    formBody['recaptchaResponse'] = key
    // Reset recaptcha so that user can take it again next time
    recaptchaRef.current.reset()
    const result = await registerUser(formBody)

    if (result.ok) {
      const currentQuery = Router.query
      const query: Query = {
        ...currentQuery,
        phone: registrationData.phone,
        source: REGISTER_REF,
      }

      if (window.fbq) {
        const data: UserProps = await result.json()
        window.fbq(
          'trackCustom',
          FB_EVENTS.SIGNUP_COMPLETED,
          {},
          { eventID: data?.id?.toString() }
        )
        track(
          SegmentEventEnum.CONVERSION_SIGNUP_COMPLETED,
          getSegmentTrackingDict(data)
        )
      }

      if (next) query.next = next

      Router.push({
        pathname: '/register/verify-otp/',
        query,
      })
      changeRegistrationData({
        loading: false,
      })
    } else if (result.status === 429) {
      setShowRatelimitModal(true)
      changeRegistrationData({
        loading: false,
      })
    } else if (result.status === 403) {
      showErrorAlert({
        text: t`You have been blocked. Please contact support if you think it is a mistake.`,
      })
      changeRegistrationData({
        loading: false,
      })
    } else {
      const error = await result.json()
      changeRegistrationData({
        errors: error,
        loading: false,
      })
    }
  }

  const handleRegistrationFormSubmit = async (
    e: FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault()
    if (registrationData.loading) return
    changeRegistrationData({
      loading: true,
    })

    const errors = validateRegistrationFormData(registrationData)
    if (Object.keys(errors).length) {
      changeRegistrationData({
        errors,
        loading: false,
      })
      return
    }
    await recaptchaRef.current.execute()
  }

  useEffect(() => {
    changeRegistrationData({ phone: props.phone })
  }, [props.phone])

  return (
    <>
      <RegistrationDetails
        {...registrationData}
        role={role}
        recaptchaRef={recaptchaRef}
        handleCaptcha={handleCaptcha}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        handleCheckboxChange={handleCheckboxChange}
        handleRegistrationFormSubmit={handleRegistrationFormSubmit}
      />
      <RatelimitErrorModal
        isOpen={showRatelimitModal}
        setIsOpen={setShowRatelimitModal}
      />
    </>
  )
}
